import { hierarchy, treemap, treemapBinary } from 'd3-hierarchy';
import type { HierarchyRectangularNode } from 'd3-hierarchy';
import { select } from 'd3-selection';

import { consumeIfPanelOutsideCloseGesture } from '~/shared/utils/panelOutsideCloseGesture';

import { isVolumeManaged } from '../../lib/classification';
import { managedInnerRatio, isUnattachedVolume } from '../../lib/bubble/bubbleHelpers';
import { volumeBubbleStyleTokens as vb } from '../../lib/bubble/volumeBubbleStyles';
import {
  ACTIVATION_ORDER,
  activationColor,
  activationLabel,
  REASON_PRIORITY,
  colorForSupport,
  resolveSupportLabel,
} from '../../lib/classification';
import { formatBytes, truncate, volumeDisplayName } from '../../lib/formatBytes';
import { buildTreemapWrappers, type TreemapItem } from '../../lib/treemap/buildTreemapWrappers';
import { groupVolumesByAccount } from '../../lib/hierarchy/wrapByAccount';
import { ttRow, smallVolumesTooltipHtml, volumeTooltipHtml } from '../../lib/tooltipHtml';
import { ancestryChipsFromVolume } from '../../lib/bubble/ancestryChips';
import {
  explorationCanvasEmptyStateHtml,
  explorationCanvasEmptyVolumesMessage,
} from '../explorationCanvasChrome';
import type {
  DrillLocation,
  ExplorationSecondary,
  ExplorationVolume,
  ViewMode,
} from '../../types/exploration';

interface TreemapHierarchyRoot {
  children: TreemapItem[];
}

export interface TreemapRenderOptions {
  view: ViewMode;
  secondary: ExplorationSecondary;
  groupByAccount: boolean;
  tagKey?: string;
  volumes: ExplorationVolume[];
  onSelectVolume: (volume: ExplorationVolume) => void;
  onDrill: (location: DrillLocation) => void;
  showTooltip: (html: string, x: number, y: number) => void;
  hideTooltip: () => void;
}

export function renderTreemapView(container: HTMLElement, options: TreemapRenderOptions): () => void {
  container.innerHTML = '';
  container.classList.add('treemap-canvas');

  const W = container.clientWidth;
  if (!W) return () => {};

  const { volumes, view } = options;
  if (!volumes.length) {
    options.hideTooltip();
    container.innerHTML = explorationCanvasEmptyStateHtml(explorationCanvasEmptyVolumesMessage);
    return () => {};
  }

  const mode = options.secondary === 'region' ? 'region' : 'hierarchy';
  let wrappers = buildTreemapWrappers(volumes, mode, view);

  if (options.groupByAccount) {
    const acctSections: Array<{ accountName: string; wrappers: ReturnType<typeof buildTreemapWrappers> }> = [];
    const byAcct = groupVolumesByAccount(volumes);
    [...byAcct.entries()].forEach(([acctName, vols]) => {
      const inner = buildTreemapWrappers(vols, mode, view);
      acctSections.push({ accountName: acctName, wrappers: inner });
    });
    acctSections.sort(
      (a, b) =>
        b.wrappers.reduce((s, w) => s + w.totalSize, 0) -
        a.wrappers.reduce((s, w) => s + w.totalSize, 0)
    );
    wrappers = [];
    acctSections.forEach((sec) => {
      sec.wrappers.forEach((w) => {
        w._accountName = sec.accountName;
      });
      wrappers.push({
        _accountHeader: sec.accountName,
        _accountSize: sec.wrappers.reduce((s, w) => s + w.totalSize, 0),
        _accountCount: sec.wrappers.reduce((s, w) => s + w.totalCount, 0),
        name: '',
        kind: '',
        cards: [],
        totalSize: 0,
        totalCount: 0,
      });
      wrappers.push(...sec.wrappers);
    });
  }

  const CARD_HDR_H = 56;
  const CARD_PAD_X = 12;
  const CARD_PAD_Y = 10;
  const CARD_GAP_X = 14;
  const CARD_GAP_Y = 14;
  const WRAP_HDR_H = 40;
  const WRAP_PAD_X = 16;
  const WRAP_PAD_Y = 14;
  const WRAP_GAP = 22;
  const TOP_PAD = 16;
  const SIDE_PAD = 12;
  const ACCT_HDR_H = 38;

  const MIN_VOL_GIB = 50;
  const MIN_RECT_PX = 14;
  const K_PX2_PER_GIB = (MIN_RECT_PX * MIN_RECT_PX) / MIN_VOL_GIB;
  const cellArea = (sizeGiB: number) => Math.max(sizeGiB, MIN_VOL_GIB) * K_PX2_PER_GIB;

  const CARD_MIN_W = 200;
  const CARD_MIN_H = 110;
  const CARD_ASPECT = 1.35;

  const FLOW_W = Math.max(W - SIDE_PAD * 2, 600);
  const INNER_W = FLOW_W - WRAP_PAD_X * 2;
  const MAX_CARD_CONTENT_W = INNER_W;

  wrappers.forEach((w) => {
    if (w._accountHeader) return;
    w.cards.forEach((card) => {
      const big: ExplorationVolume[] = [];
      const small: ExplorationVolume[] = [];
      card.volumes.forEach((v) => {
        if ((v.size || 0) < MIN_VOL_GIB) small.push(v);
        else big.push(v);
      });
      big.sort((a, b) => (b.size || 0) - (a.size || 0));
      const smallTotal = small.reduce((s, v) => s + (v.size || 0), 0);
      const items: TreemapItem[] = big.map((v) => ({
        kind: 'volume',
        value: cellArea(v.size || 0),
        volume: v,
      }));
      if (small.length) {
        items.push({
          kind: 'small-agg',
          value: cellArea(smallTotal),
          smallVols: small,
          smallTotal,
        });
      }
      items.sort((a, b) => b.value - a.value);
      card._tmItems = items;

      const totalArea = items.reduce((s, it) => s + it.value, 0);
      let contentH = Math.sqrt(totalArea / CARD_ASPECT);
      let contentW = totalArea / contentH;
      if (contentW > MAX_CARD_CONTENT_W) {
        contentW = MAX_CARD_CONTENT_W;
        contentH = totalArea / contentW;
      }
      card.contentW = contentW;
      card.contentH = contentH;
      card.boxW = Math.max(CARD_MIN_W, contentW + CARD_PAD_X * 2);
      card.boxH = Math.max(CARD_MIN_H, contentH + CARD_HDR_H + CARD_PAD_Y);
    });
  });

  let curY = TOP_PAD;
  wrappers.forEach((w) => {
    if (w._accountHeader) {
      w.x = SIDE_PAD;
      w.y = curY;
      w.w = FLOW_W;
      w.h = ACCT_HDR_H;
      curY += w.h + 4;
      return;
    }
    let nx = 0;
    let ny = 0;
    let rowH = 0;
    w.cards.forEach((card) => {
      if (nx + card.boxW! > INNER_W && nx > 0) {
        nx = 0;
        ny += rowH + CARD_GAP_Y;
        rowH = 0;
      }
      card.relX = nx;
      card.relY = ny;
      nx += card.boxW! + CARD_GAP_X;
      rowH = Math.max(rowH, card.boxH!);
    });
    const innerH = ny + rowH;
    w.x = SIDE_PAD;
    w.y = curY;
    w.w = FLOW_W;
    w.h = WRAP_HDR_H + innerH + WRAP_PAD_Y * 2;
    curY += w.h + WRAP_GAP;
  });
  const totalH = curY + 16;

  const svg = select(container)
    .append('svg')
    .attr('width', '100%')
    .attr('height', totalH)
    .attr('viewBox', `0 0 ${W} ${totalH}`)
    .style('display', 'block');

  const palette =
    view === 'activation'
      ? {
          color: activationColor,
          order: ACTIVATION_ORDER,
          label: activationLabel,
          field: 'activationStatus' as const,
        }
      : {
          color: (k: string) => colorForSupport(k),
          order: ['supported', ...REASON_PRIORITY],
          label: (k: string) => resolveSupportLabel(k || ''),
          field: 'supportStatus' as const,
        };

  wrappers.forEach((w) => {
    if (w._accountHeader) {
      const aG = svg.append('g').attr('transform', `translate(${w.x},${w.y})`);
      aG.append('rect')
        .attr('width', w.w ?? 0)
        .attr('height', w.h ?? 0)
        .attr('rx', 8)
        .attr('fill', 'var(--color-sky-blue)')
        .attr('fill-opacity', 0.4)
        .attr('stroke', 'none');
      aG.append('text')
        .attr('x', WRAP_PAD_X)
        .attr('y', 14)
        .attr('font-size', 10)
        .attr('font-weight', 600)
        .attr('fill', 'var(--color-gray-600)')
        .attr('font-family', 'var(--font-sans)')
        .attr('letter-spacing', '0.04em')
        .text('ACCOUNT');
      aG.append('text')
        .attr('x', WRAP_PAD_X)
        .attr('y', 32)
        .attr('font-size', 14)
        .attr('font-weight', 600)
        .attr('fill', 'var(--color-ocean-blue)')
        .attr('font-family', 'var(--font-display)')
        .text(w._accountHeader);
      aG.append('text')
        .attr('x', w.w! - WRAP_PAD_X)
        .attr('y', 32)
        .attr('text-anchor', 'end')
        .attr('font-size', 12)
        .attr('fill', 'var(--color-gray-700)')
        .attr('font-family', 'var(--font-sans)')
        .text(
          `${w._accountCount!.toLocaleString()} ${w._accountCount === 1 ? 'volume' : 'volumes'} · ${formatBytes(w._accountSize!)}`
        );
      return;
    }

    const wG = svg.append('g').attr('transform', `translate(${w.x},${w.y})`);
    const wrapColor =
      w.kind === 'ec2'
        ? 'var(--color-dark-magenta)'
        : w.kind === 'unattached'
          ? 'var(--color-gray-700)'
          : 'var(--color-vivd-purple)';
    const wrapBg =
      w.kind === 'ec2'
        ? 'var(--color-magnolia-accent)'
        : w.kind === 'unattached'
          ? 'var(--color-gray-200)'
          : 'var(--color-magnolia)';

    wG.append('rect')
      .attr('width', w.w ?? 0)
      .attr('height', w.h ?? 0)
      .attr('rx', 12)
      .attr('fill', wrapBg)
      .attr('fill-opacity', 0.3)
      .attr('stroke', 'none');

    wG.append('text')
      .attr('x', WRAP_PAD_X)
      .attr('y', 16)
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .attr('fill', 'var(--color-gray-600)')
      .attr('font-family', 'var(--font-sans)')
      .attr('letter-spacing', '0.04em')
      .text(
        w.kind === 'k8s'
          ? 'CLUSTER'
          : w.kind === 'ec2'
            ? 'EC2'
            : w.kind === 'unattached'
              ? 'UNATTACHED'
              : 'GROUP'
      );
    wG.append('text')
      .attr('x', WRAP_PAD_X)
      .attr('y', 32)
      .attr('font-size', 15)
      .attr('font-weight', 600)
      .attr('fill', wrapColor)
      .attr('font-family', 'var(--font-display)')
      .text(truncate(w.name, 60));
    wG.append('text')
      .attr('x', w.w! - WRAP_PAD_X)
      .attr('y', 32)
      .attr('text-anchor', 'end')
      .attr('font-size', 12)
      .attr('fill', 'var(--color-gray-700)')
      .attr('font-family', 'var(--font-sans)')
      .text(
        `${w.totalCount.toLocaleString()} ${w.totalCount === 1 ? 'volume' : 'volumes'} · ${formatBytes(w.totalSize)}`
      );

    const stage = wG.append('g').attr('transform', `translate(${WRAP_PAD_X},${WRAP_HDR_H + WRAP_PAD_Y})`);

    w.cards.forEach((card) => {
      const cG = stage.append('g').attr('transform', `translate(${card.relX},${card.relY})`);

      cG.append('rect')
        .attr('width', card.boxW ?? 0)
        .attr('height', card.boxH ?? 0)
        .attr('rx', 8)
        .attr('fill', 'var(--color-white)')
        .attr('stroke', 'var(--color-primary-100)')
        .attr('stroke-width', 1)
        .attr('cursor', 'pointer')
        .on('click', (e) => {
          if (consumeIfPanelOutsideCloseGesture(e)) {
            return;
          }
          e.stopPropagation();
          let location: DrillLocation | null = null;
          if (mode === 'region') {
            // region drilling not yet supported in bubble view
          } else if (w.kind === 'k8s') {
            location = {
              structural: {
                kind: 'cluster',
                top: { kind: 'cluster', name: w.name },
                nodeGroup: card.name !== w.name ? card.name : undefined,
              },
            };
          } else if (w.kind === 'ec2') {
            location = { structural: { kind: 'cluster', top: { kind: 'ec2' } } };
          } else if (w.kind === 'unattached') {
            location = { structural: { kind: 'cluster', top: { kind: 'unattached' } } };
          }
          if (location) options.onDrill(location);
        });

      cG.append('text')
        .attr('x', CARD_PAD_X)
        .attr('y', 20)
        .attr('font-size', 13)
        .attr('font-weight', 600)
        .attr('fill', 'var(--color-primary-900)')
        .attr('font-family', 'var(--font-display)')
        .text(truncate(card.name, Math.max(20, Math.floor(card.boxW! / 7))));
      cG.append('text')
        .attr('x', CARD_PAD_X)
        .attr('y', 36)
        .attr('font-size', 11)
        .attr('fill', 'var(--color-gray-600)')
        .attr('font-family', 'var(--font-sans)')
        .text(
          `${card.totalCount.toLocaleString()} ${card.totalCount === 1 ? 'volume' : 'volumes'} · ${formatBytes(card.totalSize)}`
        );

      const barW = Math.min(110, card.boxW! * 0.4);
      const barX = card.boxW! - CARD_PAD_X - barW;
      const barY = 28;
      const total = card.totalCount;
      const segs = palette.order.filter((k) => card.statusCounts.has(k));
      [...card.statusCounts.keys()].forEach((k) => {
        if (!segs.includes(k)) segs.push(k);
      });
      cG.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', 6)
        .attr('rx', 3)
        .attr('fill', 'var(--color-gray-200)');
      let bx = barX;
      segs.forEach((k) => {
        const n = card.statusCounts.get(k) || 0;
        const segW = (n / total) * barW;
        cG.append('rect')
          .attr('x', bx)
          .attr('y', barY)
          .attr('width', segW)
          .attr('height', 6)
          .attr('fill', palette.color(k))
          .append('title')
          .text(`${palette.label(k)} · ${n} ${n === 1 ? 'volume' : 'volumes'}`);
        bx += segW;
      });

      const tmX = CARD_PAD_X;
      const tmY = CARD_HDR_H;
      const tmW = card.contentW!;
      const tmH = card.contentH!;
      if (tmW < 8 || tmH < 8 || card.volumes.length === 0) return;

      const tmRoot = hierarchy<TreemapHierarchyRoot | TreemapItem>({
        children: card._tmItems ?? [],
      })
        .sum((d) => ('value' in d ? d.value : 0) || 0)
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      treemap<TreemapHierarchyRoot | TreemapItem>()
        .tile(treemapBinary)
        .size([tmW, tmH])
        .paddingInner(3)
        .round(true)(tmRoot);

      const tmG = cG.append('g').attr('transform', `translate(${tmX},${tmY})`);
      tmRoot.leaves().forEach((leaf) => {
        const rect = leaf as HierarchyRectangularNode<TreemapItem | TreemapHierarchyRoot>;
        const lw = rect.x1 - rect.x0;
        const lh = rect.y1 - rect.y0;
        if (lw < 1 || lh < 1) return;
        const item = rect.data as TreemapItem;
        const cellG = tmG
          .append('g')
          .attr('class', 'treemap-cell')
          .attr('transform', `translate(${rect.x0},${rect.y0})`);

        if (item.kind === 'small-agg') {
          const small = item.smallVols;
          const smallTotal = item.smallTotal;
          cellG
            .append('rect')
            .attr('width', lw)
            .attr('height', lh)
            .attr('rx', 2)
            .attr('fill', 'var(--color-gray-200)')
            .attr('stroke', 'var(--color-gray-400)')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '4,3')
            .attr('cursor', 'help')
            .attr('pointer-events', 'all')
            .on('mouseenter', (e) => {
              const sorted = [...small].sort((a, b) => (b.size || 0) - (a.size || 0));
              const shown = sorted.slice(0, 12);
              options.showTooltip(
                smallVolumesTooltipHtml(shown, smallTotal, sorted.length),
                e.clientX,
                e.clientY
              );
            })
            .on('mouseleave', () => options.hideTooltip());
          if (lw > 50 && lh > 24) {
            const fs = Math.max(9, Math.min(11, lh * 0.22));
            cellG
              .append('text')
              .attr('x', lw / 2)
              .attr('y', lh / 2 - 2)
              .attr('text-anchor', 'middle')
              .attr('font-size', fs)
              .attr('font-weight', 500)
              .attr('fill', 'var(--color-gray-700)')
              .attr('font-family', 'var(--font-sans)')
              .attr('pointer-events', 'none')
              .text(`${small.length} small`);
            cellG
              .append('text')
              .attr('x', lw / 2)
              .attr('y', lh / 2 + fs + 1)
              .attr('text-anchor', 'middle')
              .attr('font-size', Math.max(9, fs - 1))
              .attr('fill', 'var(--color-gray-600)')
              .attr('font-family', 'var(--font-sans)')
              .attr('font-variant-numeric', 'tabular-nums')
              .attr('pointer-events', 'none')
              .text(formatBytes(smallTotal));
          }
          return;
        }

        const v = item.volume;
        const managed = isVolumeManaged(v);

        cellG
          .append('rect')
          .attr('width', lw)
          .attr('height', lh)
          .attr('rx', 2)
          .attr('fill', managed ? vb.volume.managed.outerFill : isUnattachedVolume(v) ? vb.volume.unattached.fill : 'var(--color-gray-300)')
          .attr('fill-opacity', managed ? 1 : 0.9)
          .attr('stroke', managed ? vb.volume.managed.outerStroke : palette.color(v[palette.field]))
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', managed ? '2,2' : null)
          .attr('cursor', 'pointer')
          .attr('pointer-events', 'all')
          .on('mouseenter', (e) => {
            const ancestry = ancestryChipsFromVolume(v, {
              secondary: options.secondary,
              tagKey: options.tagKey,
              groupByAccount: options.groupByAccount,
            });
            options.showTooltip(volumeTooltipHtml(v, ancestry), e.clientX, e.clientY);
          })
          .on('mouseleave', () => options.hideTooltip())
          .on('click', (e) => {
            if (consumeIfPanelOutsideCloseGesture(e)) {
              return;
            }
            e.stopPropagation();
            options.onSelectVolume(v);
          });

        if (managed && lw > 8 && lh > 8) {
          const ratio = managedInnerRatio(v);
          const innerW = lw * ratio;
          const innerH = lh * ratio;
          cellG
            .append('rect')
            .attr('x', (lw - innerW) / 2)
            .attr('y', (lh - innerH) / 2)
            .attr('width', innerW)
            .attr('height', innerH)
            .attr('rx', 2)
            .attr('fill', vb.volume.managed.innerFill)
            .attr('stroke', vb.volume.managed.outerStroke)
            .attr('stroke-width', 1)
            .attr('pointer-events', 'none');
        }

        if (lw > 56 && lh > 26) {
          const fs = Math.max(9, Math.min(11, lh * 0.22));
          const charW = fs * 0.55;
          const maxChars = Math.max(6, Math.floor((lw - 8) / charW));
          if (lh >= 36) {
            cellG
              .append('text')
              .attr('x', lw / 2)
              .attr('y', lh / 2 - 1)
              .attr('text-anchor', 'middle')
              .attr('font-size', fs)
              .attr('font-weight', 500)
              .attr('fill', managed ? 'var(--color-primary-900)' : 'var(--color-gray-800)')
              .attr('font-family', 'var(--font-sans)')
              .attr('pointer-events', 'none')
              .text(truncate(volumeDisplayName(v), maxChars));
            cellG
              .append('text')
              .attr('x', lw / 2)
              .attr('y', lh / 2 + fs + 1)
              .attr('text-anchor', 'middle')
              .attr('font-size', Math.max(9, fs - 1))
              .attr('fill', managed ? 'var(--color-primary-700)' : 'var(--color-gray-600)')
              .attr('font-family', 'var(--font-sans)')
              .attr('font-variant-numeric', 'tabular-nums')
              .attr('pointer-events', 'none')
              .text(formatBytes(v.size));
          } else {
            cellG
              .append('text')
              .attr('x', lw / 2)
              .attr('y', lh / 2 + fs * 0.35)
              .attr('text-anchor', 'middle')
              .attr('font-size', fs)
              .attr('font-weight', 500)
              .attr('fill', managed ? 'var(--color-primary-900)' : 'var(--color-gray-800)')
              .attr('font-family', 'var(--font-sans)')
              .attr('pointer-events', 'none')
              .text(truncate(volumeDisplayName(v), maxChars));
          }
        }
      });
    });
  });

  return () => {
    svg.remove();
    container.innerHTML = '';
    container.classList.remove('treemap-canvas');
  };
}
